import React, { useCallback } from 'react';
import ReactFlow, {
  MiniMap,
  Controls,
  Background,
  useNodesState,
  useEdgesState,
  addEdge,
  ConnectionLineType,
  Handle,
  Position,
} from 'reactflow';
import 'reactflow/dist/style.css';
import {
  UserIcon,
  UserGroupIcon,
  BuildingOfficeIcon,
  EnvelopeIcon,
  ClockIcon,
  CheckCircleIcon,
} from '@heroicons/react/24/outline';

// Helper function to get unique identifier for user
const getUserIdentifier = (user) => {
  if (!user) return '';
  // Try multiple identifier sources for better matching
  if (user.id) return user.id;
  if (user.email) return user.email.toLowerCase();
  if (user.name) return user.name.toLowerCase();
  if (user.firstName && user.lastName) {
    return `${user.firstName.toLowerCase()}_${user.lastName.toLowerCase()}`;
  }
  // Fallback to any available identifier
  return JSON.stringify(user).toLowerCase();
};

// Custom Node Component
const HierarchyNode = ({ data, isConnectable }) => {
  const { user, level, isCurrentUser, isManager, hasSubordinates, isCollapsed } = data;

  const getNodeStyle = () => {
    if (isCurrentUser) {
      return 'bg-gradient-to-br from-blue-50 to-blue-200 border-3 border-blue-500 shadow-xl ring-2 ring-blue-200';
    } else if (level === 0) {
      return 'bg-gradient-to-br from-purple-50 to-purple-200 border-3 border-purple-400 shadow-xl ring-2 ring-purple-200';
    } else if (isManager) {
      return 'bg-gradient-to-br from-green-50 to-green-200 border-3 border-green-400 shadow-xl ring-2 ring-green-200';
    } else {
      return 'bg-white border-3 border-gray-300 shadow-lg ring-1 ring-gray-200';
    }
  };

  const getIconColor = () => {
    if (isCurrentUser) return 'text-blue-600';
    if (level === 0) return 'text-purple-600';
    if (isManager) return 'text-green-600';
    return 'text-gray-600';
  };

  const reportsCount = typeof hasSubordinates === 'number' ? hasSubordinates : (hasSubordinates ? 1 : 0);

  return (
    <div className={`${getNodeStyle()} rounded-lg p-3 min-w-[260px] max-w-[300px] relative transform hover:scale-105 transition-all duration-200`}>
      {/* Connection Handles */}
      <Handle
        type="target"
        position={Position.Top}
        id="top"
        style={{ 
          background: '#6b7280', 
          width: 12, 
          height: 12,
          border: '2px solid white',
          boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
        }}
      />
      <Handle
        type="source"
        position={Position.Bottom}
        id="bottom"
        style={{ 
          background: '#6b7280', 
          width: 12, 
          height: 12,
          border: '2px solid white',
          boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
        }}
      />
      
      {/* Header */}
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center space-x-2">
          {isManager ? (
            <UserGroupIcon className={`w-5 h-5 ${getIconColor()}`} />
          ) : (
            <UserIcon className={`w-5 h-5 ${getIconColor()}`} />
          )}
          <div>
            <h3 className="font-bold text-gray-900 text-base">
              {user.name}
              {isCurrentUser && (
                <span className="ml-2 text-xs bg-blue-600 text-white px-3 py-1 rounded-full font-semibold">You</span>
              )}
            </h3>
            {user.isTeamLead && (
              <span className="text-xs bg-purple-600 text-white px-3 py-1 rounded-full mt-1 inline-block font-semibold">
                Team Lead
              </span>
            )}
          </div>
        </div>
        {reportsCount > 0 && (
          <div className="text-xs text-gray-700 bg-gray-200 px-3 py-1 rounded-full font-semibold">
            {reportsCount} {reportsCount === 1 ? 'report' : 'reports'}
          </div>
        )}
      </div>

      {/* Details */}
      <div className="space-y-2 text-sm text-gray-700">
        <div className="flex items-center space-x-2">
          <EnvelopeIcon className="w-4 h-4 text-gray-500" />
          <span className="truncate font-medium">{user.email}</span>
        </div>
        {user.department && (
          <div className="flex items-center space-x-2">
            <BuildingOfficeIcon className="w-4 h-4 text-gray-500" />
            <span className="truncate font-medium">{user.department}</span>
          </div>
        )}
        {user.relationship && (
          <div className="text-xs bg-gray-300 text-gray-800 px-3 py-1 rounded-full inline-block font-semibold">
            {user.relationship}
          </div>
        )}
      </div>

      {/* Stats Section */}
      {(user.stats || user.attendance || user.attendanceStats) && (
        <div className="mt-3 pt-3 border-t border-gray-300 space-y-2 mb-2">
          {/* Attendance Stats */}
          {(user.stats?.attendance || user.attendance || user.attendanceStats) && (
            <div className="bg-blue-50 rounded-lg p-2">
              <div className="flex items-center space-x-1 mb-1">
                <ClockIcon className="w-4 h-4 text-blue-600" />
                <span className="text-xs font-bold text-blue-900">Attendance</span>
              </div>
              <div className="grid grid-cols-2 gap-1 text-xs">
                <div className="flex flex-col">
                  <span className="text-gray-600">Hours</span>
                  <span className="font-bold text-blue-700">{(user.stats?.attendance?.totalHours ?? user.attendance?.totalHours ?? user.attendanceStats?.totalHours ?? 0)}h</span>
                </div>
                <div className="flex flex-col">
                  <span className="text-gray-600">Rate</span>
                  <span className="font-bold text-blue-700">{(user.stats?.attendance?.attendanceRate ?? user.attendance?.attendanceRate ?? user.attendanceStats?.attendanceRate ?? 0)}%</span>
                </div>
                <div className="flex flex-col">
                  <span className="text-gray-600">Days</span>
                  <span className="font-bold text-blue-700">{(user.stats?.attendance?.daysPresent ?? user.attendance?.daysPresent ?? user.attendanceStats?.daysPresent ?? 0)}/{(user.stats?.attendance?.workingDays ?? user.attendance?.workingDays ?? user.attendanceStats?.workingDays ?? 0)}</span>
                </div>
                <div className="flex flex-col">
                  <span className="text-gray-600">Avg/Day</span>
                  <span className="font-bold text-blue-700">{(user.stats?.attendance?.avgHours ?? user.attendance?.avgHours ?? user.attendanceStats?.avgHours ?? 0)}h</span>
                </div>
              </div>
            </div>
          )}
          
          {/* ClickUp Stats */}
          {user.stats?.clickup && user.stats.clickup.clickupEnabled && (
            <div className="bg-purple-50 rounded-lg p-2">
              <div className="flex items-center justify-between mb-1">
                <div className="flex items-center space-x-1">
                  <CheckCircleIcon className="w-4 h-4 text-purple-600" />
                  <span className="text-xs font-bold text-purple-900">ClickUp</span>
                </div>
                <span className="text-xs font-bold text-purple-700 bg-purple-200 px-2 py-0.5 rounded-full">
                  {user.stats.clickup.taskCount} tasks
                </span>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Expand/Collapse Toggle Button for managers */}
      {isManager && reportsCount > 0 && (
        <button
          onClick={(e) => {
            e.stopPropagation();
            data.onToggleCollapse(user);
          }}
          className="absolute -bottom-4 left-1/2 transform -translate-x-1/2 z-10 flex items-center justify-center gap-1 bg-white border border-gray-300 hover:border-blue-500 hover:text-blue-600 text-gray-700 text-xs px-3 py-1 rounded-full shadow-md font-semibold transition-all hover:scale-105 cursor-pointer"
        >
          {isCollapsed ? (
            <>
              <svg className="w-3.5 h-3.5 text-blue-500" fill="none" viewBox="0 0 24 24" strokeWidth="2.5" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" />
              </svg>
              <span>Show {reportsCount}</span>
            </>
          ) : (
            <>
              <svg className="w-3.5 h-3.5 text-red-500" fill="none" viewBox="0 0 24 24" strokeWidth="2.5" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 12h-15" />
              </svg>
              <span>Hide {reportsCount}</span>
            </>
          )}
        </button>
      )}
    </div>
  );
};

// Node Types
const nodeTypes = {
  hierarchyNode: HierarchyNode,
};

const TeamHierarchyFlow = ({ hierarchyData, loading }) => {
  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);
  const [collapsedNodes, setCollapsedNodes] = React.useState(new Set());

  const handleToggleCollapse = useCallback((user) => {
    const userIdentifier = getUserIdentifier(user);
    setCollapsedNodes((prev) => {
      const next = new Set(prev);
      if (next.has(userIdentifier)) {
        next.delete(userIdentifier);
      } else {
        next.add(userIdentifier);
      }
      return next;
    });
  }, []);

  // Generate nodes and edges from hierarchy data
  const generateFlowData = useCallback((data) => {
    if (!data) return { nodes: [], edges: [] };

    const nodes = [];
    const edges = [];
    let yPosition = 0;
    const xCenter = 500; // Center position for vertical layout
    const nodeWidth = 320; // Actual node width
    const nodeHeight = 280; // Estimated node height
    const minHorizontalSpacing = nodeWidth + 50; // Minimum space between nodes
    const minVerticalSpacing = nodeHeight + 80; // Minimum space between levels
    
    // Track users that will appear in lower levels to prevent them from appearing in upper levels
    const usersInLowerLevels = new Set();
    // Track users that have already been processed to prevent duplicates
    const processedUsers = new Set();

    // Helper function to check if user will appear in lower levels
    const isUserInLowerLevels = (user) => {
      const identifier = getUserIdentifier(user);
      return usersInLowerLevels.has(identifier);
    };

    // Helper function to mark user as appearing in lower levels
    const markUserInLowerLevels = (user) => {
      const identifier = getUserIdentifier(user);
      usersInLowerLevels.add(identifier);
    };

    // Helper function to check if user has already been processed
    const isUserProcessed = (user) => {
      const identifier = getUserIdentifier(user);
      return processedUsers.has(identifier);
    };

    // Helper function to mark user as processed
    const markUserAsProcessed = (user) => {
      const identifier = getUserIdentifier(user);
      processedUsers.add(identifier);
    };

    // First pass: Recursively collect all users that will appear in subordinates tree
    const collectLowerLevelUsers = (subordinatesTree, level = 0) => {
      if (!subordinatesTree || subordinatesTree.length === 0) return;
      
      subordinatesTree.forEach(subordinate => {
        const subordinateId = getUserIdentifier(subordinate);
        if (collapsedNodes.has(subordinateId)) {
          // If subordinate is collapsed, do not traverse deeper for lower level exclusions
          return;
        }
        
        // Mark as lower level if we're at level 3 or deeper (level 3 = sub-subordinates)
        // Level 0 = managers above current user, Level 1 = current user, Level 2 = direct subordinates, Level 3 = sub-subordinates
        if (level >= 3) {
          markUserInLowerLevels(subordinate);
        }
        
        // Recursively collect their subordinates (go as deep as needed)
        const subSubordinates = subordinate.subordinates || subordinate.directReports || subordinate.teamMembers || subordinate.reports || [];
        if (subSubordinates.length > 0) {
          collectLowerLevelUsers(subSubordinates, level + 1); // Recursive call with incremented level
        }
      });
    };

    // Helper function to check if a position overlaps with existing nodes
    const checkOverlap = (x, y, existingNodes) => {
      return existingNodes.some(node => {
        const nodeX = node.position.x;
        const nodeY = node.position.y;
        const horizontalOverlap = Math.abs(x - nodeX) < minHorizontalSpacing;
        const verticalOverlap = Math.abs(y - nodeY) < minVerticalSpacing;
        return horizontalOverlap && verticalOverlap;
      });
    };

    // Helper function to find non-overlapping position
    const findNonOverlappingPosition = (preferredX, preferredY, existingNodes, maxAttempts = 10) => {
      let x = preferredX;
      let y = preferredY;
      let attempts = 0;
      
      while (checkOverlap(x, y, existingNodes) && attempts < maxAttempts) {
        x += minHorizontalSpacing * (attempts % 2 === 0 ? 1 : -1);
        if (attempts % 2 === 1) {
          y += minVerticalSpacing / 2;
        }
        attempts++;
      }
      
      return { x, y };
    };

    // Helper function to create a node (with duplicate checking)
    const createNode = (user, level, isCurrentUser = false, isManager = false, customPosition = null) => {
      const userIdentifier = getUserIdentifier(user);
      
      // Skip if user will appear in lower levels (prevent duplicates at higher levels)
      // Level 2 users should be skipped if they appear in level 3
      if (level <= 2 && isUserInLowerLevels(user)) {
        console.log(`Skipping user at level ${level}: ${user.name || user.email} (will appear in lower level)`);
        return null;
      }
      
      // Mark user as processed only after we've determined they should be created
      markUserAsProcessed(user);
      
      // Check for different possible property names for subordinates
      const subordinates = user.subordinates || user.directReports || user.teamMembers || user.reports || [];
      // Compute count; for current user node (level 1), fallback to root subordinates list
      let hasSubordinatesCount = Array.isArray(subordinates) ? subordinates.length : 0;
      if (level === 1 && hasSubordinatesCount === 0 && data && Array.isArray(data.subordinatesTree)) {
        hasSubordinatesCount = data.subordinatesTree.length;
      }

      const isCollapsed = collapsedNodes.has(userIdentifier);
      
      let position;
      if (customPosition) {
        // Check for overlaps and adjust if necessary
        const adjustedPosition = findNonOverlappingPosition(customPosition.x, customPosition.y, nodes);
        position = adjustedPosition;
      } else {
        position = { x: xCenter, y: yPosition };
      }
      
      return {
        id: `node-${userIdentifier}`,
        type: 'hierarchyNode',
        position,
        data: {
          user,
          level,
          isCurrentUser,
          isManager,
          hasSubordinates: hasSubordinatesCount,
          isCollapsed,
          onToggleCollapse: handleToggleCollapse
        },
        draggable: true,
      };
    };

    // First pass: Collect all users that will appear in lower levels
    collectLowerLevelUsers(data.subordinatesTree);

    // Collect all users by level for row-based layout
    const level1Users = []; // Upper chain managers
    const level2Users = []; // Direct subordinates (second level)
    const level3Users = []; // Sub-subordinates (third level)
    const level4Users = []; // Sub-sub-subordinates (fourth level)
    const level2ToLevel3Map = new Map(); // Map level 2 users to their level 3 subordinates
    const level3ToLevel4Map = new Map(); // Map level 3 users to their level 4 subordinates

    // Collect level 1 (upper chain managers)
    if (data.upperChain && data.upperChain.length > 0) {
      data.upperChain.forEach((manager) => {
        if (!isUserInLowerLevels(manager)) {
          level1Users.push(manager);
        }
      });
    }

    // Collect level 2 (direct subordinates) and level 3 (sub-subordinates)
    if (data.subordinatesTree && data.subordinatesTree.length > 0) {
      data.subordinatesTree.forEach((subordinate) => {
        if (!isUserInLowerLevels(subordinate)) {
          level2Users.push(subordinate);
          
          const isSubordinateCollapsed = collapsedNodes.has(getUserIdentifier(subordinate));
          
          // Collect level 3 subordinates for this level 2 user
          const subSubordinates = subordinate.subordinates || subordinate.directReports || subordinate.teamMembers || subordinate.reports || [];
          if (subSubordinates.length > 0 && !isSubordinateCollapsed) {
            const level3Subs = [];
            subSubordinates.forEach((subSub) => {
              level3Subs.push(subSub);
              level3Users.push(subSub);

              const isSubSubCollapsed = collapsedNodes.has(getUserIdentifier(subSub));
              const level4Subs = subSub.subordinates || subSub.directReports || subSub.teamMembers || subSub.reports || [];
              if (level4Subs.length > 0 && !isSubSubCollapsed) {
                level3ToLevel4Map.set(subSub, level4Subs);
                level4Subs.forEach((lvl4) => {
                  level4Users.push(lvl4);
                });
              }
            });
            level2ToLevel3Map.set(subordinate, level3Subs);
          }
        }
      });
    }

    // Helper function to calculate row position (centered horizontally)
    const calculateRowPositions = (count, startY) => {
      if (count === 0) return [];
      const startX = xCenter - (count - 1) * (minHorizontalSpacing / 2);
      const positions = [];
      for (let i = 0; i < count; i++) {
        positions.push({
          x: startX + i * minHorizontalSpacing,
          y: startY
        });
      }
      return positions;
    };

    // Create nodes for level 1 (first row)
    const level1Nodes = [];
    const level1Positions = calculateRowPositions(level1Users.length, yPosition);
    level1Users.forEach((manager, index) => {
      const managerNode = createNode(manager, 0, false, true, level1Positions[index]);
      if (managerNode) {
        nodes.push(managerNode);
        level1Nodes.push(managerNode);
      }
    });
    let currentY = yPosition;
    if (level1Users.length > 0) {
      currentY += minVerticalSpacing;
    }

    // Create current user node (second row, centered)
    const currentUserNode = createNode(data.user, 1, true, true, { x: xCenter, y: currentY });
    if (currentUserNode) {
      nodes.push(currentUserNode);
      
      // Create edges from level 1 managers to current user
      level1Nodes.forEach((managerNode) => {
        edges.push({
          id: `edge-${managerNode.id}-${currentUserNode.id}`,
          source: managerNode.id,
          target: currentUserNode.id,
          sourceHandle: 'bottom',
          targetHandle: 'top',
          type: 'smoothstep',
          animated: true,
          style: {
            stroke: '#8b5cf6',
            strokeWidth: 2,
            strokeDasharray: '5,3',
            filter: 'drop-shadow(0 1px 2px rgba(139, 92, 246, 0.2))'
          },
          markerEnd: {
            type: 'arrowclosed',
            color: '#8b5cf6',
            width: 15,
            height: 15,
            strokeWidth: 2,
          },
        });
      });
      
      currentY += minVerticalSpacing;
    }

    // Create nodes for level 2 (third row)
    const level2Nodes = [];
    const level2Positions = calculateRowPositions(level2Users.length, currentY);
    level2Users.forEach((subordinate, index) => {
      const subordinateNode = createNode(
        subordinate,
        2,
        false,
        true,
        level2Positions[index]
      );
      if (subordinateNode) {
        nodes.push(subordinateNode);
        level2Nodes.push(subordinateNode);
        
        // Create edge from current user to subordinate
        if (currentUserNode) {
          edges.push({
            id: `edge-${currentUserNode.id}-${subordinateNode.id}`,
            source: currentUserNode.id,
            target: subordinateNode.id,
            sourceHandle: 'bottom',
            targetHandle: 'top',
            type: 'smoothstep',
            animated: true,
            style: {
              stroke: '#059669',
              strokeWidth: 2,
              strokeDasharray: '5,3',
              filter: 'drop-shadow(0 1px 2px rgba(5, 150, 105, 0.2))'
            },
            markerEnd: {
              type: 'arrowclosed',
              color: '#059669',
              width: 15,
              height: 15,
              strokeWidth: 2,
            },
          });
        }
      }
    });
    if (level2Users.length > 0) {
      currentY += minVerticalSpacing;
    }

    // Create nodes for level 3 (fourth row)
    const level3Positions = calculateRowPositions(level3Users.length, currentY);
    let level3Index = 0;
    
    level2Users.forEach((subordinate) => {
      const subSubordinates = level2ToLevel3Map.get(subordinate) || [];
      const subordinateNode = level2Nodes.find(node => {
        const nodeUser = node.data.user;
        return getUserIdentifier(nodeUser) === getUserIdentifier(subordinate);
      });
      
      if (subordinateNode && subSubordinates.length > 0) {
        subSubordinates.forEach((subSub) => {
          const subSubSubordinates = subSub.subordinates || subSub.directReports || subSub.teamMembers || subSub.reports || [];
          const subSubNode = createNode(
            subSub,
            3,
            false,
            subSubSubordinates && subSubSubordinates.length > 0,
            level3Positions[level3Index]
          );
          
          if (subSubNode) {
            nodes.push(subSubNode);
            
            // Create edge from level 2 to level 3
            edges.push({
              id: `edge-${subordinateNode.id}-${subSubNode.id}`,
              source: subordinateNode.id,
              target: subSubNode.id,
              sourceHandle: 'bottom',
              targetHandle: 'top',
              type: 'smoothstep',
              animated: true,
              style: {
                stroke: '#374151',
                strokeWidth: 1.5,
                strokeDasharray: '4,2',
                filter: 'drop-shadow(0 1px 1px rgba(55, 65, 81, 0.2))'
              },
              markerEnd: {
                type: 'arrowclosed',
                color: '#374151',
                width: 12,
                height: 12,
                strokeWidth: 1.5,
              },
            });
          }
          level3Index++;
        });
      }
    });

    if (level3Users.length > 0) {
      currentY += minVerticalSpacing;
    }

    // Create nodes for level 4 (fifth row)
    const level4Positions = calculateRowPositions(level4Users.length, currentY);
    let level4Index = 0;

    level3Users.forEach((level3User) => {
      const level3Node = nodes.find(node => getUserIdentifier(node.data.user) === getUserIdentifier(level3User));
      const level4Subs = level3ToLevel4Map.get(level3User) || [];

      if (level3Node && level4Subs.length > 0) {
        level4Subs.forEach((level4User) => {
          const deeperSubs = level4User.subordinates || level4User.directReports || level4User.teamMembers || level4User.reports || [];
          const level4Node = createNode(
            level4User,
            4,
            false,
            deeperSubs && deeperSubs.length > 0,
            level4Positions[level4Index]
          );

          if (level4Node) {
            nodes.push(level4Node);

            edges.push({
              id: `edge-${level3Node.id}-${level4Node.id}`,
              source: level3Node.id,
              target: level4Node.id,
              sourceHandle: 'bottom',
              targetHandle: 'top',
              type: 'smoothstep',
              animated: true,
              style: {
                stroke: '#1f2937',
                strokeWidth: 1.5,
                strokeDasharray: '4,2',
                filter: 'drop-shadow(0 1px 1px rgba(31, 41, 55, 0.2))'
              },
              markerEnd: {
                type: 'arrowclosed',
                color: '#1f2937',
                width: 12,
                height: 12,
                strokeWidth: 1.5,
              },
            });
          }
          level4Index++;
        });
      }
    });

    return { nodes, edges };
  }, [collapsedNodes, handleToggleCollapse]);

  // Update nodes and edges when hierarchy data or collapsed nodes change
  React.useEffect(() => {
    if (hierarchyData) {
      const { nodes: newNodes, edges: newEdges } = generateFlowData(hierarchyData);
      setNodes(newNodes);
      setEdges(newEdges);
    }
  }, [hierarchyData, generateFlowData, collapsedNodes, setNodes, setEdges]);

  const onConnect = useCallback(
    (params) => setEdges((eds) => addEdge(params, eds)),
    [setEdges]
  );

  const connectionLineType = ConnectionLineType.SmoothStep;

  if (loading) {
    return (
      <div className="flex justify-center items-center h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600"></div>
      </div>
    );
  }

  if (!hierarchyData) {
    return (
      <div className="flex justify-center items-center h-96">
        <div className="text-center">
          <UserGroupIcon className="w-16 h-16 text-gray-400 mx-auto mb-3" />
          <p className="text-gray-600">No hierarchy data available</p>
        </div>
      </div>
    );
  }

  return (
    <div className="h-[900px] w-full border-2 border-gray-300 rounded-xl shadow-inner bg-gradient-to-br from-gray-50 to-gray-100">
      <ReactFlow
        nodes={nodes}
        edges={edges}
        onNodesChange={onNodesChange}
        onEdgesChange={onEdgesChange}
        onConnect={onConnect}
        connectionLineType={connectionLineType}
        nodeTypes={nodeTypes}
        fitView
        fitViewOptions={{
          padding: 0.2,
          includeHiddenNodes: false,
          minZoom: 0.25,
          maxZoom: 1.5,
        }}
        attributionPosition="bottom-left"
        className="bg-transparent"
        defaultEdgeOptions={{
          type: 'smoothstep',
          animated: true,
          style: {
            strokeWidth: 2,
            filter: 'drop-shadow(0 1px 2px rgba(0, 0, 0, 0.1))'
          },
          markerEnd: {
            type: 'arrowclosed',
            width: 15,
            height: 15,
            strokeWidth: 2,
          },
        }}
      >
        <Controls 
          position="top-right"
          showZoom={true}
          showFitView={true}
          showInteractive={false}
        />
        <MiniMap
          nodeColor={(node) => {
            if (node.data.isCurrentUser) return '#3b82f6';
            if (node.data.level === 0) return '#8b5cf6';
            if (node.data.isManager) return '#10b981';
            return '#6b7280';
          }}
          nodeStrokeWidth={3}
          zoomable
          pannable
          position="bottom-left"
          style={{
            background: 'rgba(255, 255, 255, 0.8)',
            border: '1px solid #e5e7eb',
          }}
        />
        <Background 
          color="#e5e7eb" 
          gap={25} 
          size={1.5}
          variant="dots"
        />
      </ReactFlow>
    </div>
  );
};

export default TeamHierarchyFlow;
